import classNames from "classnames";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useLoadRecipients } from "react-migration/domains/sites/hooks";
import { Stores, useGlobalState, useTranslation } from "react-migration/lib";
import { LetterStep } from "src/js/stores/letter";
import { loadRecipients, updateCurrentStep } from "src/js/stores/letter/actions";

import { Button, Loading } from "@landtechnologies/components";

import {
  SELECT_RECIPIENTS_FORM_ID,
  SelectRecipientsForm,
  SelectRecipientsFormData,
  mergeFormDataWithRecipientData,
} from "../forms";
import { SelectRecipientsFormProvider } from "../forms/SelectRecipientsForm/SelectRecipientsFormProvider";
import { Footer, Header, LetterStepper } from "./components";
import { LetterEventName, logLetterEvent } from "./helpers/logLetterEvent";

type SelectEditableRecipientsStepContentProps = {
  onSubmit: (formData: SelectRecipientsFormData) => void;
};

export const SelectEditableRecipientsStepContent = ({
  onSubmit,
}: SelectEditableRecipientsStepContentProps) => {
  const { t } = useTranslation();

  const [{ selectedRecipientIds }] = useGlobalState(Stores.letter);

  const { formState } = useFormContext<SelectRecipientsFormData>();

  const isNextEnabled =
    selectedRecipientIds && selectedRecipientIds?.length > 0 && formState.isValid;

  return (
    <div
      data-testid="select-editable-recipients-step"
      className="atlas-h-screen atlas-w-full atlas-flex atlas-flex-col"
    >
      <Header />
      <LetterStepper currentStep={LetterStep.SelectRecipients} />
      <div
        data-testid="select-recipients-form-container"
        className={classNames("atlas-flex", "atlas-grow", "atlas-w-full", "atlas-overflow-hidden")}
      >
        <SelectRecipientsForm onSubmit={onSubmit} />
      </div>
      <Footer>
        <div className="atlas-flex atlas-flex-grow atlas-justify-end">
          <Button.Primary disabled={!isNextEnabled} type="submit" form={SELECT_RECIPIENTS_FORM_ID}>
            {t("sites.letters.next_create_letter_button")}
          </Button.Primary>
        </div>
      </Footer>
    </div>
  );
};

export const SelectEditableRecipientsStep = () => {
  const [{ selectedStageId }] = useGlobalState(Stores.letter);
  const [{ filters }] = useGlobalState(Stores.sitesPipeline);
  const [{ siteCardId: siteId }] = useGlobalState(Stores.site);

  const { loading, recipients } = useLoadRecipients({
    siteIds: siteId ? [siteId] : undefined,
    stageIds: selectedStageId ? [selectedStageId] : undefined,
    filters,
  });

  const recipientsFormData: SelectRecipientsFormData | undefined = useMemo(() => {
    return {
      recipients:
        recipients?.map(({ fullAddress: _f, propertyAddress: _p, ...recipient }) => ({
          ...recipient,
          isSelected: recipient.isGranted,
        })) ?? [],
    };
  }, [recipients]);

  const onFormSubmit = (formData: SelectRecipientsFormData) => {
    if (!recipients) return;

    const updatedRecipients = mergeFormDataWithRecipientData(recipients, formData);

    loadRecipients(updatedRecipients);
    updateCurrentStep(LetterStep.TemplateEditor);
    logLetterEvent({
      name: LetterEventName.LETTERS_FORM_SELECT_RECIPIENTS_STEP_CREATE_LETTER_BUTTON_CLICKED,
    });
  };

  return (
    <>
      {loading && (
        <div
          className={classNames(
            "atlas-h-full",
            "atlas-w-full",
            "atlas-flex",
            "atlas-justify-center",
            "atlas-items-center",
            "atlas-overflow-hidden"
          )}
        >
          <Loading />
        </div>
      )}
      {!loading && (
        <SelectRecipientsFormProvider data={recipientsFormData}>
          <SelectEditableRecipientsStepContent onSubmit={onFormSubmit} />
        </SelectRecipientsFormProvider>
      )}
    </>
  );
};
